// Option-based variant selection (color / size / one vendor-named custom
// option). Pure helpers, unit-tested in variantOptions.test.ts. Products without
// `options` never reach these — they keep the flat variant pickers.

export type OptionType = 'color' | 'size' | 'custom'
export type ProductOption = { type: OptionType; name?: string; values: { value: string; hex?: string }[] }
export type OptionValues = Partial<Record<OptionType, string>>
export type ValueState = 'available' | 'soldOut' | 'missing'

export function getOptions(product: any): ProductOption[] {
  if (!Array.isArray(product?.options)) return []
  return product.options.filter((o: any) => o && Array.isArray(o.values) && o.values.length > 0)
}

export function hasOptions(product: any): boolean {
  return getOptions(product).length > 0
}

export function optionLabel(option: Pick<ProductOption, 'type' | 'name'>): string {
  if (option.type === 'color') return 'Өнгө'
  if (option.type === 'size') return 'Хэмжээ'
  return option.name || 'Сонголт'
}

export function isVariantInStock(product: any, variant: any): boolean {
  return !product?.isTrackable || (variant?.stock ?? 0) > 0
}

// Variants carrying a value for every option. Anything else (e.g. a variant
// added through the standalone variant API) can't be reached from the picker.
export function optionVariants(product: any): any[] {
  const options = getOptions(product)
  return (product?.variants || []).filter((v: any) => v?.optionValues && options.every((o) => typeof v.optionValues[o.type] === 'string'))
}

export function findVariant(product: any, selected: OptionValues): any | null {
  const options = getOptions(product)
  if (options.length === 0 || options.some((o) => !selected[o.type])) return null
  return optionVariants(product).find((v) => options.every((o) => v.optionValues[o.type] === selected[o.type])) ?? null
}

export function valueState(product: any, selected: OptionValues, type: OptionType, value: string): ValueState {
  const options = getOptions(product)
  const candidates = optionVariants(product).filter((v) =>
    v.optionValues[type] === value &&
    options.every((o) => o.type === type || !selected[o.type] || v.optionValues[o.type] === selected[o.type]),
  )
  if (candidates.length === 0) return 'missing'
  return candidates.some((v) => isVariantInStock(product, v)) ? 'available' : 'soldOut'
}

// Variant whose price and photos to show when the picked combination doesn't exist.
export function fallbackVariant(product: any, selected: OptionValues, changedType: OptionType): any | null {
  const variants = optionVariants(product)
  const sameColor = selected.color ? variants.find((v) => v.optionValues.color === selected.color) : undefined
  return sameColor ?? variants.find((v) => v.optionValues[changedType] === selected[changedType]) ?? null
}

export function rewardOptionProduct(reward: any): any | null {
  const variants: any[] = reward?.variants || []
  const options = getOptions(reward).map((o) => ({ ...o, values: o.values.filter(({ value }) => variants.some((v) => v?.optionValues?.[o.type] === value)) }))
  const product = { ...reward, options, variant: variants.find((v) => v.id === reward?.defaultVariantId) }
  return hasOptions(product) && optionVariants(product).length === variants.length ? product : null
}

export type ColorSwatch = { value: string; hex?: string; soldOut: boolean }

export function colorSwatches(product: any): ColorSwatch[] {
  const color = getOptions(product).find((o) => o.type === 'color')
  if (!color) return []
  return color.values.flatMap(({ value, hex }) => {
    const state = valueState(product, {}, 'color', value)
    return state === 'missing' ? [] : [{ value, hex, soldOut: state === 'soldOut' }]
  })
}

export function defaultColor(product: any, swatches: ColorSwatch[]): string | null {
  const own = swatches.find((c) => c.value === product?.variant?.optionValues?.color)
  if (own && !own.soldOut) return own.value
  return (swatches.find((c) => !c.soldOut) ?? own ?? swatches[0])?.value ?? null
}

export function colorVariant(product: any, color: string): any | null {
  const variants = optionVariants(product).filter((v) => v.optionValues.color === color)
  const inStock = variants.filter((v) => isVariantInStock(product, v))
  return inStock.find((v) => v.id === product?.variant?.id) ?? inStock[0] ?? variants[0] ?? null
}

export function visibleSwatches(swatches: ColorSwatch[], active: string | null, max: number): ColorSwatch[] {
  const activeIndex = swatches.findIndex((c) => c.value === active)
  return activeIndex >= max ? [...swatches.slice(0, max - 1), swatches[activeIndex]] : swatches.slice(0, max)
}
